import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Container,
  Heading,
  Text,
  Button,
  VStack,
  HStack,
  Grid,
  GridItem,
  useColorModeValue,
  Circle,
  Image,
  useBreakpointValue,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalCloseButton,
  useDisclosure,
} from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import { getHomeData } from "../../services/homeService";

const HeroSection = () => {
  const textColor = useColorModeValue("gray.700", "gray.200");
  const headingColor = useColorModeValue("gray.900", "white");
  const navigate = useNavigate();
  const { isOpen, onOpen, onClose } = useDisclosure();

  // State for API data
  const [homeData, setHomeData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Image slider state
  const [currentSlide, setCurrentSlide] = useState(0);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [videoThumbnails, setVideoThumbnails] = useState({});

  // Generate thumbnail from video file
  const generateVideoThumbnail = (videoUrl, videoId) => {
    return new Promise((resolve) => {
      const video = document.createElement('video');
      video.crossOrigin = 'anonymous';
      video.currentTime = 1; // Get frame at 1 second
      
      video.onloadeddata = () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        
        const thumbnail = canvas.toDataURL('image/jpeg', 0.8);
        setVideoThumbnails(prev => ({
          ...prev,
          [videoId]: thumbnail
        }));
        resolve(thumbnail);
      };
      
      video.onerror = () => {
        // If video loading fails, keep original URL
        resolve(videoUrl);
      };
      
      video.src = videoUrl;
    });
  };

  // Helper function to check if a slide is a video
  const isVideoFile = (slide) => {
    if (!slide) return false;
    
    // First check if the API explicitly marks it as video
    if (slide.isVideo) return true;
    
    // Then check URL patterns
    const url = slide.url || slide.videoUrl || '';
    const videoExtensions = ['.mp4', '.webm', '.ogg', '.avi', '.mov', '.wmv', '.flv', '.mkv'];
    const urlLower = url.toLowerCase();
    return videoExtensions.some(ext => urlLower.includes(ext)) || 
           urlLower.includes('youtube') || 
           urlLower.includes('vimeo') ||
           urlLower.includes('video');
  };

  // Helper function to get YouTube thumbnail
  const getYouTubeThumbnail = (url) => {
    const videoId = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\n?#]+)/);
    if (videoId) {
      return `https://img.youtube.com/vi/${videoId[1]}/maxresdefault.jpg`;
    }
    return null;
  };

  // Helper function to get Vimeo thumbnail
  const getVimeoThumbnail = (url) => {
    const videoId = url.match(/vimeo\.com\/(\d+)/);
    if (videoId) {
      // Note: Vimeo thumbnails require API call, for now return a placeholder
      return `https://vumbnail.com/${videoId[1]}.jpg`;
    }
    return null;
  };

  // Helper function to get video thumbnail
  const getVideoThumbnail = (slide) => {
    if (!isVideoFile(slide)) return slide.url;
    
    // First priority: Generated thumbnail from our cache
    if (videoThumbnails[slide.id]) return videoThumbnails[slide.id];
    
    // Second priority: API provided thumbnail
    if (slide.thumbnail) return slide.thumbnail;
    
    // Third priority: Platform specific thumbnails
    const videoUrl = slide.videoUrl || slide.url;
    if (videoUrl.includes('youtube') || videoUrl.includes('youtu.be')) {
      return getYouTubeThumbnail(videoUrl);
    } else if (videoUrl.includes('vimeo')) {
      return getVimeoThumbnail(videoUrl);
    }
    
    // For direct video files, try to generate thumbnail
    if (videoUrl && !videoThumbnails[slide.id]) {
      generateVideoThumbnail(videoUrl, slide.id);
    }
    
    // Fallback: return the original URL (might be a thumbnail or video file)
    return slide.url;
  };

  // Get the actual video URL for playing
  const getVideoPlayUrl = (slide) => {
    return slide.videoUrl || slide.url;
  };

  // Handle play button click
  const handlePlayClick = (slide) => {
    if (isVideoFile(slide)) {
      setSelectedVideo(slide);
      onOpen();
    }
  };

  // Responsive values
  const headingSize = useBreakpointValue({ base: "xl", md: "2xl", lg: "3xl" });
  const textSize = useBreakpointValue({ base: "md", md: "lg" });
  const buttonSize = useBreakpointValue({ base: "md", md: "lg" });
  const sliderHeight = useBreakpointValue({
    base: "300px",
    md: "400px",
    lg: "500px",
  });
  const playButtonSize = useBreakpointValue({
    base: "50px",
    md: "60px",
    lg: "70px",
  });

  // Fetch home data on component mount
  useEffect(() => {
    const fetchHomeData = async () => {
      try {
        setLoading(true);
        const data = await getHomeData();
        setHomeData(data);
        setError(null);
      } catch (err) {
        console.error('Error fetching home data:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchHomeData();
  }, []);

  // Get slider images from API data or fallback images
  const sliderImages = useMemo(() => {
    const images = homeData?.carouselImages || [];
    return images;
  }, [homeData]);

  // Auto-play functionality - only run if we have images
  useEffect(() => {
    if (sliderImages.length > 0) {
      const interval = setInterval(() => {
        setCurrentSlide((prev) => (prev + 1) % sliderImages.length);
      }, 4000);

      return () => clearInterval(interval);
    }
  }, [sliderImages.length]);

  // Generate thumbnails for video slides when data loads
  useEffect(() => {
    if (sliderImages.length > 0) {
      sliderImages.forEach((slide) => {
        if (isVideoFile(slide) && !slide.thumbnail && !videoThumbnails[slide.id]) {
          const videoUrl = slide.videoUrl || slide.url;
          // Only generate for direct video files (not YouTube/Vimeo)
          if (videoUrl && !videoUrl.includes('youtube') && !videoUrl.includes('vimeo') && !videoUrl.includes('youtu.be')) {
            generateVideoThumbnail(videoUrl, slide.id);
          }
        }
      });
    }
  }, [sliderImages, videoThumbnails]);

  if (loading) {
    return (
      <Box
        position="relative"
        minH={{ base: "auto", md: "00vh" }}
        py={{ base: 4, md: 8, lg: 10 }}
        bg="transparent"
      >
        <Container maxW="7xl" position="relative" zIndex="2">
          <VStack spacing={4} py={20}>
            <Text>Loading home content...</Text>
          </VStack>
        </Container>
      </Box>
    );
  }

  // Show error state
  if (error) {
    return (
      <Box
        position="relative"
        minH={{ base: "auto", md: "00vh" }}
        py={{ base: 4, md: 8, lg: 10 }}
        bg="transparent"
      >
        <Container maxW="7xl" position="relative" zIndex="2">
          <VStack spacing={4} py={20}>
            <Text color="red.500">Error loading home content: {error}</Text>
          </VStack>
        </Container>
      </Box>
    );
  }

  return (
    <Box
      position="relative"
      minH={{ base: "auto", md: "00vh" }}
      py={{ base: 4, md: 8, lg: 10 }}
      bg="transparent"
    >
      {/* Background overlays */}
      <Box
        position="absolute"
        top="0"
        left="0"
        right="0"
        bottom="0"
        backgroundSize="cover"
        backgroundPosition="center"
        backgroundRepeat="no-repeat"
        opacity="0.1"
        zIndex="1"
      />

      <Box
        position="absolute"
        top="0"
        left="0"
        right="0"
        bottom="0"
        bg="rgba(59, 130, 246, 0.02)"
        zIndex="1"
      />

      <Container
        maxW="7xl"
        position="relative"
        zIndex="2"
        px={{ base: 4, md: 6 }}
      >
        <Grid
          templateColumns={{ base: "1fr", lg: "1fr 1fr" }}
          gap={{ base: 8, md: 10, lg: 12 }}
          alignItems="center"
        >
          {/* Left Content */}
          <GridItem order={{ base: 2, lg: 1 }}>
            <VStack align="flex-start" spacing={{ base: 6, md: 8 }}>
              <Box>
                <Heading
                  as="h1"
                  size={headingSize}
                  color={headingColor}
                  lineHeight="1.2"
                  fontWeight="bold"
                  mb={{ base: 4, md: 6 }}
                >
                  {homeData?.headerDesc?.title || "Transforming Government with Trusted Technology"}
                </Heading>
                <Text
                  fontSize={textSize}
                  color={textColor}
                  lineHeight="1.6"
                  borderLeft="4px solid"
                  borderColor="blue.400"
                  pl={4}
                >
                  {homeData?.headerDesc?.description || "Providing innovative technology solutions with a focus on inclusivity and excellence for over 27 years."}
                </Text>
              </Box>

              {/* <Grid
                templateColumns={{ base: "1fr", sm: "repeat(2, 1fr)" }}
                gap={{ base: 4, md: 6 }}
                w="full"
              >
                {stats.map((stat, index) => (
                  <GridItem key={index}>
                    <VStack align="flex-start" spacing={2}>
                      <Text
                        fontSize={{ base: "2xl", md: "3xl" }}
                        fontWeight="bold"
                        color={stat.color}
                        lineHeight="1"
                      >
                        {stat.number}
                      </Text>
                      <Text
                        fontSize={{ base: "xs", md: "sm" }}
                        color={textColor}
                        fontWeight="medium"
                      >
                        {stat.label}
                      </Text>
                    </VStack>
                  </GridItem>
                ))}
              </Grid> */}

              <VStack
                spacing={4}
                pt={4}
                w="full"
                align={{ base: "stretch", sm: "flex-start" }}
              >
                <HStack
                  spacing={4}
                  flexDirection={{ base: "column", sm: "row" }}
                  w={{ base: "full", sm: "auto" }}
                >
                  <Button
                    size={buttonSize}
                    bg="#000B8C"
                    color="white"
                    _hover={{ bg: "blue.700" }}
                    _active={{ bg: "blue.800" }}
                    px={{ base: 6, md: 8 }}
                    py={{ base: 4, md: 6 }}
                    borderRadius="full"
                    onClick={() => navigate("/our-services")}
                    w={{ base: "full", sm: "auto" }}
                  >
                    Explore Solutions
                  </Button>
                  <Button
                    size={buttonSize}
                    variant="ghost"
                    color={textColor}
                    _hover={{ bg: "gray.100" }}
                    px={{ base: 6, md: 8 }}
                    py={{ base: 4, md: 6 }}
                    borderRadius="full"
                    onClick={() => navigate("/careers")}
                    w={{ base: "full", sm: "auto" }}
                  >
                    Join Our Team
                  </Button>
                </HStack>
              </VStack>
            </VStack>
          </GridItem>

          {/* Right Content - 3D Perspective Image Slider */}
          <GridItem order={{ base: 1, lg: 2 }}>
            <Box
              position="relative"
              h={sliderHeight}
              w="full"
              p={{ base: 4, md: 8 }}
              perspective={{ base: "800px", md: "1000px" }}
            >
              {/* 3D Platform/Base */}
              <Box
                position="absolute"
                bottom="-10px"
                left="15%"
                w="70%"
                h={{ base: "15px", md: "20px" }}
                bg="linear-gradient(135deg, rgba(0, 0, 0, 0.1) 0%, rgba(0, 0, 0, 0.2) 100%)"
                borderRadius="50%"
                filter="blur(8px)"
                zIndex="0"
                transform="rotateX(60deg)"
              />

              {/* Dynamic Floating Cards */}
              {/* {floatingStats.map((stat, index) => (
                <Box
                  key={stat.id || index}
                  position="absolute"
                  top={index === 0 ? { base: "20%", md: "30%", lg: "40%" } : undefined}
                  bottom={index === 1 ? { base: "20%", md: "30%", lg: "20%" } : undefined}
                  left={index === 0 ? { base: "5%", md: "2%", lg: "0%" } : { base: "5%", md: "8%", lg: "10%" }}
                  bg="white"
                  pl={index === 0 ? { base: 2, md: 3, lg: 4 } : { base: 2, md: 3, lg: 4 }}
                  pt={index === 0 ? { base: 1, md: 2, lg: 0 } : { base: 1, md: 2, lg: 0 }}
                  borderRadius="2xl"
                  minW={{ base: "120px", md: "140px", lg: "160px" }}
                  zIndex="15"
                  sx={{
                    boxShadow:
                      "0 20px 60px rgba(0, 0, 0, 0.15), 0 8px 20px rgba(0, 0, 0, 0.1)",
                    backdropFilter: "blur(10px)",
                    bg: "rgba(255, 255, 255, 0.95)",
                    border: "1px solid rgba(255, 255, 255, 0.3)",
                    transition: "all 0.3s ease",
                  }}
                  _hover={{
                    transform: "translateY(-3px)",
                    boxShadow:
                      "0 25px 80px rgba(0, 0, 0, 0.2), 0 10px 30px rgba(0, 0, 0, 0.15)",
                  }}
                >
                  <VStack spacing={0} align="flex-start">
                    <Text
                      fontSize={{ base: "lg", md: "xl", lg: "2xl" }}
                      fontWeight="bold"
                      color="#000B8C"
                    >
                      {stat.number}
                    </Text>
                    <Text
                      fontSize={{ base: "xs", md: "sm" }}
                      color="gray.600"
                      fontWeight="medium"
                    >
                      {stat.label}
                    </Text>
                  </VStack>
                </Box>
              ))} */}

              {/* Main 3D Container */}
              <Box
                position="relative"
                w="full"
                h="full"
                sx={{
                  transform: {
                    base: "rotateY(-4deg) rotateX(1deg)",
                    md: "rotateY(-6deg) rotateX(1.5deg)",
                    lg: "rotateY(-8deg) rotateX(2deg)",
                  },
                  transformStyle: "preserve-3d",
                }}
              >
                {/* Main Slider Container with 3D Perspective and Asymmetric Cut */}
                <Box
                  position="relative"
                  w="full"
                  h="full"
                  overflow="hidden"
                  sx={{
                    clipPath: {
                      base: "polygon(0% 0%, 100% 0%, 98% 100%, 0% 100%)",
                      md: "polygon(0% 0%, 100% 0%, 96% 100%, 0% 100%, 10% 100%, 0% 3%)",
                      lg: "polygon(0% 0%, 100% 0%, 95% 100%, 0% 100%, 15% 100%, 0% 5%)",
                    },
                    borderRadius: {
                      base: "20px",
                      md: "25px",
                      lg: "30px 30px 30px 0px",
                    },
                    transform: {
                      base: "translateZ(10px)",
                      md: "translateZ(15px)",
                      lg: "translateZ(20px)",
                    },
                  }}
                  _before={{
                    content: '""',
                    position: "absolute",
                    top: "0",
                    left: "0",
                    right: "0",
                    bottom: "0",
                    background:
                      "linear-gradient(135deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.05) 100%)",
                    zIndex: "10",
                    pointerEvents: "none",
                  }}
                >
                  {/* Left Side 3D Edge - Hidden on mobile */}
                  <Box
                    position="absolute"
                    top="0"
                    left="0"
                    w={{ base: "0%", md: "20%", lg: "25%" }}
                    h="full"
                    bg="linear-gradient(90deg, rgba(0,0,0,0.3) 0%, rgba(0,0,0,0.1) 100%)"
                    sx={{
                      clipPath: "polygon(0% 25%, 100% 0%, 100% 100%, 0% 100%)",
                    }}
                    zIndex="1"
                    display={{ base: "none", md: "block" }}
                  />

                  {/* Image Slider */}
                  {sliderImages.length > 0 ? (
                    sliderImages.map((slide, index) => {               
                      return (
                        <Box
                          key={slide.id || index}
                          position="absolute"
                          top="0"
                          left="0"
                          w="full"
                          h="full"
                          opacity={currentSlide === index ? 1 : 0}
                          transition="opacity 0.8s ease-in-out"
                          zIndex={currentSlide === index ? 2 : 1}
                        >
                      {isVideoFile(slide) ? (
                        // For videos, show thumbnail image
                        <Image
                          src={getVideoThumbnail(slide)}
                          alt={slide.alt}
                          w="full"
                          h="full"
                          objectFit="cover"
                          onError={(e) => {
                            // If thumbnail fails to load, try to use the original URL
                            if (e.target.src !== slide.url) {
                              e.target.src = slide.url;
                            }
                          }}
                          fallback={
                            <Box
                              w="full"
                              h="full"
                              bg="linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
                              display="flex"
                              alignItems="center"
                              justifyContent="center"
                              flexDirection="column"
                              position="relative"
                              overflow="hidden"
                            >
                              {/* Animated background pattern */}
                              <Box
                                position="absolute"
                                top="0"
                                left="0"
                                w="full"
                                h="full"
                                opacity="0.1"
                                bgImage="radial-gradient(circle, white 1px, transparent 1px)"
                                bgSize="20px 20px"
                                animation="float 6s ease-in-out infinite"
                              />
                              
                              {/* Video icon overlay */}
                              <Box
                                position="absolute"
                                top="50%"
                                left="50%"
                                transform="translate(-50%, -50%)"
                                color="rgba(255, 255, 255, 0.2)"
                                fontSize={{ base: "80px", md: "120px", lg: "150px" }}
                              >
                                🎬
                              </Box>
                              
                              {/* Content overlay */}
                              <Box
                                bg="rgba(255, 255, 255, 0.95)"
                                p={{ base: 3, md: 4 }}
                                borderRadius="lg"
                                textAlign="center"
                                zIndex="2"
                                boxShadow="0 8px 32px rgba(0, 0, 0, 0.1)"
                                backdropFilter="blur(10px)"
                                border="1px solid rgba(255, 255, 255, 0.2)"
                              >
                                <Text 
                                  color="gray.700" 
                                  fontSize={{ base: "sm", md: "md" }} 
                                  fontWeight="bold"
                                  mb={1}
                                >
                                  Video Content
                                </Text>
                                <Text 
                                  color="gray.500" 
                                  fontSize={{ base: "xs", md: "sm" }}
                                  fontWeight="medium"
                                >
                                  Click ▶ to watch
                                </Text>
                              </Box>
                            </Box>
                          }
                        />
                      ) : (
                        // For images, show normally
                        <Image
                          src={slide.url}
                          alt={slide.alt}
                          w="full"
                          h="full"
                          objectFit="cover"
                          onLoad={() => {
                          }}
                          onError={(e) => {
                            console.error(`❌ Image failed to load: ${slide.url}`, e);
                            console.log(`🔍 Image error details:`, {
                              src: e.target.src,
                              naturalWidth: e.target.naturalWidth,
                              naturalHeight: e.target.naturalHeight,
                              complete: e.target.complete
                            });
                          }}
                          fallback={
                            <Box
                              w="full"
                              h="full"
                              bg="linear-gradient(135deg, #3182ce 0%, #805ad5 100%)"
                              display="flex"
                              alignItems="center"
                              justifyContent="center"
                              flexDirection="column"
                              position="relative"
                              overflow="hidden"
                            >
                              {/* Animated background pattern */}
                              <Box
                                position="absolute"
                                top="0"
                                left="0"
                                w="full"
                                h="full"
                                opacity="0.1"
                                bgImage="radial-gradient(circle, white 1px, transparent 1px)"
                                bgSize="20px 20px"
                                animation="float 6s ease-in-out infinite"
                              />
                              
                              {/* Image icon overlay */}
                              <Box
                                position="absolute"
                                top="50%"
                                left="50%"
                                transform="translate(-50%, -50%)"
                                color="rgba(255, 255, 255, 0.2)"
                                fontSize={{ base: "80px", md: "120px", lg: "150px" }}
                              >
                                🖼️
                              </Box>
                              
                              {/* Content overlay */}
                              <Box
                                bg="rgba(255, 255, 255, 0.95)"
                                p={{ base: 3, md: 4 }}
                                borderRadius="lg"
                                textAlign="center"
                                zIndex="2"
                                boxShadow="0 8px 32px rgba(0, 0, 0, 0.1)"
                                backdropFilter="blur(10px)"
                                border="1px solid rgba(255, 255, 255, 0.2)"
                              >
                                <Text 
                                  color="gray.700" 
                                  fontSize={{ base: "sm", md: "md" }} 
                                  fontWeight="bold"
                                  mb={1}
                                >
                                  Image Loading...
                                </Text>
                                <Text 
                                  color="gray.500" 
                                  fontSize={{ base: "xs", md: "sm" }}
                                  fontWeight="medium"
                                >
                                  {slide.alt || 'Carousel Image'}
                                </Text>
                              </Box>
                            </Box>
                          }
                        />
                      )}

                      {/* 3D Gradient Overlay */}
                      <Box
                        position="absolute"
                        top="0"
                        left="0"
                        w="full"
                        h="full"
                        bg="linear-gradient(135deg, rgba(0, 0, 0, 0.4) 0%, rgba(59, 130, 246, 0.2) 50%, rgba(0, 0, 0, 0.3) 100%)"
                        zIndex="1"
                      />
                    </Box>
                      );
                    })
                  ) : (
                    // Show fallback when no slider images are available
                    <Box
                      position="absolute"
                      top="0"
                      left="0"
                      w="full"
                      h="full"
                      bg="linear-gradient(135deg, #a0aec0 0%, #718096 100%)"
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      flexDirection="column"
                    >
                      <Text color="white" fontSize="xl" fontWeight="bold" mb={2}>
                        🖼️ No Images Available
                      </Text>
                      <Text color="whiteAlpha.800" fontSize="md" textAlign="center">
                        Carousel images are loading...
                      </Text>
                    </Box>
                  )}

                  {/* Enhanced Play Button with 3D Effect - Only show for videos */}
                  {sliderImages[currentSlide] && isVideoFile(sliderImages[currentSlide]) && (
                    <Box
                      position="absolute"
                      top="50%"
                      left={{ base: "50%", md: "52%", lg: "55%" }}
                      transform="translate(-50%, -50%)"
                      zIndex="3"
                    >
                      <Circle
                        size={playButtonSize}
                        bg="rgba(59, 130, 246, 0.9)"
                        color="white"
                        cursor="pointer"
                        onClick={() => handlePlayClick(sliderImages[currentSlide])}
                        _hover={{
                          bg: "rgba(59, 130, 246, 1)",
                          transform: "scale(1.1) translateZ(5px)",
                        }}
                        transition="all 0.3s ease"
                        sx={{
                          boxShadow:
                            "0 12px 40px rgba(59, 130, 246, 0.4), inset 0 2px 10px rgba(255, 255, 255, 0.2)",
                          transform: {
                            base: "translateZ(8px)",
                            md: "translateZ(12px)",
                            lg: "translateZ(15px)",
                          },
                        }}
                      >
                        <Box
                          w="0"
                          h="0"
                          borderLeft={{
                            base: "16px solid white",
                            md: "20px solid white",
                            lg: "22px solid white",
                          }}
                          borderTop={{
                            base: "10px solid transparent",
                            md: "12px solid transparent",
                            lg: "14px solid transparent",
                          }}
                          borderBottom={{
                            base: "10px solid transparent",
                            md: "12px solid transparent",
                            lg: "14px solid transparent",
                          }}
                          ml={{ base: "4px", md: "5px", lg: "6px" }}
                        />
                      </Circle>
                    </Box>
                  )}

                  {/* 3D Highlight Edge */}
                  <Box
                    position="absolute"
                    top="0"
                    right="0"
                    w="2px"
                    h="full"
                    bg="linear-gradient(180deg, rgba(255,255,255,0.6) 0%, rgba(255,255,255,0.2) 100%)"
                    zIndex="5"
                    display={{ base: "none", md: "block" }}
                  />
                </Box>
              </Box>

              {/* Enhanced Carousel Dots with 3D Effect */}
              <HStack
                spacing={{ base: 2, md: 3 }}
                position="absolute"
                bottom={{ base: 6, md: 8, lg: 12 }}
                left="50%"
                transform="translateX(-50%) translateZ(20px)"
                zIndex="4"
              >
                {sliderImages.map((_, index) => (
                  <Circle
                    key={index}
                    size={{ base: "10px", md: "12px" }}
                    bg={currentSlide === index ? "#000B8C" : "white"}
                    opacity={currentSlide === index ? 1 : 0.6}
                    cursor="pointer"
                    transition="all 0.3s ease"
                    onClick={() => setCurrentSlide(index)}
                    sx={{
                      boxShadow:
                        currentSlide === index
                          ? "0 4px 15px rgba(59, 130, 246, 0.4)"
                          : "0 2px 8px rgba(0, 0, 0, 0.2)",
                      transform:
                        currentSlide === index ? "scale(1.2)" : "scale(1)",
                    }}
                  />
                ))}
              </HStack>
            </Box>
          </GridItem>
        </Grid>
      </Container>

      {/* Video Modal */}
      <Modal isOpen={isOpen} onClose={onClose} size="6xl" isCentered>
        <ModalOverlay bg="rgba(0, 0, 0, 0.8)" />
        <ModalContent bg="transparent" boxShadow="none" maxW="90vw" maxH="90vh">
          <ModalCloseButton
            color="white"
            size="lg"
            _hover={{ bg: "rgba(255, 255, 255, 0.2)" }}
            zIndex="10"
          />
          {selectedVideo && (
            <Box
              w="100%"
              h="100%"
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              {/* Handle different video types */}
              {(() => {
                const videoUrl = getVideoPlayUrl(selectedVideo);
                if (videoUrl.includes('youtube') || videoUrl.includes('youtu.be')) {
                  return (
                    <Box
                      as="iframe"
                      src={videoUrl.replace('watch?v=', 'embed/')}
                      w="100%"
                      h="500px"
                      maxW="800px"
                      allowFullScreen
                      frameBorder="0"
                    />
                  );
                } else if (videoUrl.includes('vimeo')) {
                  return (
                    <Box
                      as="iframe"
                      src={videoUrl.replace('vimeo.com/', 'player.vimeo.com/video/')}
                      w="100%"
                      h="500px"
                      maxW="800px"
                      allowFullScreen
                      frameBorder="0"
                    />
                  );
                } else {
                  return (
                    <Box
                      as="video"
                      src={videoUrl}
                      w="100%"
                      h="auto"
                      maxW="800px"
                      maxH="500px"
                      controls
                      autoPlay
                    />
                  );
                }
              })()}
            </Box>
          )}
        </ModalContent>
      </Modal>

      {/* Enhanced CSS Animation with 3D Effects */}
      <style jsx>{`
        @keyframes float {
          0%,
          100% {
            transform: translateY(0px) translateZ(0px);
          }
          50% {
            transform: translateY(-10px) translateZ(5px);
          }
        }

        @keyframes rotate3d {
          0% {
            transform: rotateY(-8deg) rotateX(2deg);
          }
          50% {
            transform: rotateY(-6deg) rotateX(1deg);
          }
          100% {
            transform: rotateY(-8deg) rotateX(2deg);
          }
        }

        @media (max-width: 768px) {
          @keyframes rotate3d {
            0% {
              transform: rotateY(-4deg) rotateX(1deg);
            }
            50% {
              transform: rotateY(-2deg) rotateX(0.5deg);
            }
            100% {
              transform: rotateY(-4deg) rotateX(1deg);
            }
          }
        }
      `}</style>
    </Box>
  );
};

export default HeroSection;
